import { Location, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbCollapse, NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { BaseFormComponent } from 'components/form/base-form';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { Observable, forkJoin } from 'rxjs';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { ClassifierCard, ClassifierValueCard } from 'types/classifier.interface';
import { isAdminUser, sortByCodeOrder } from 'util/user.utility';
import {
  ClientCard,
  CreateReaderCardRequest,
  ReaderCardByClientId,
  ReaderCardById,
  ResearchTopicByClientIdCard,
  ResearchTopicByIdCard,
  UpdateReaderCardRequest,
  UpdateResearchTopicActivityRequest
} from 'types/client.interface';
import { FILE_OBJECT_TYPE_DOCTORAL } from 'types/file.interface';
import { BackofficeReadingRoomSubmissionCard } from 'types/service.interface';
import { formatDate, formatTime } from 'util/date.utility';
import { getControlsWithErrors } from 'util/form.utility';
import { TopicInfoComponent } from 'components/topic-info/topic-info.component';

export const SCIENTIFIC_DEGREE_CLASSIFIER_CODE = 'ScientificDegree';
export const EDUCATION_CLASSIFIER_CODE = 'EducationLevel';
export const EDUCATION_CODE_HIGHER = 'EducationLevel.Higher';
export const SCIENTIFIC_DEGREE_CODE_DOCTORAL = 'ScientificDegree.Doctoral';
import { map } from 'rxjs/operators';

const EDUCATION_ORDER = ['EducationLevel.Basic', 'EducationLevel.Secondary', 'EducationLevel.Higher'] as const;

const DEGREE_ORDER = ['ScientificDegree.Bachelor', 'ScientificDegree.Master', 'ScientificDegree.Doctoral'] as const;

@Component({
  selector: 'app-readers-card-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    NgbCollapse,
    NgClass,
    SpinnerComponent,
    NgxDropzoneModule,
    ConfirmModalComponent,
    FileUploadComponent,
    TooltipComponent,
    NgbPagination,
    TopicInfoComponent
  ],
  templateUrl: './readers-card-form.component.html',
  styleUrls: ['../../form/base-form.scss', './readers-card-form.component.scss']
})
export class ReadersCardFormComponent extends BaseFormComponent implements OnInit {
  public readonly doctoralFileType = FILE_OBJECT_TYPE_DOCTORAL;
  @Output() readersCardUpdated: EventEmitter<void> = new EventEmitter<void>();
  @Input() client!: ClientCard;
  @Input() readersCard?: ReaderCardByClientId;
  @Input() researchTopics: ResearchTopicByClientIdCard[] = [];
  @Output() refetchTopics: EventEmitter<void> = new EventEmitter<void>();
  @Output() formChanged = new EventEmitter<boolean>();
  public readonly formatDate = formatDate;
  public readonly formatTime = formatTime;
  isResearchTopicsCollapsed = false;
  researchTopicsCollapsed: boolean[] = [];
  isAddReadersCardMode: boolean = false;
  scientificDegrees?: ClassifierValueCard[];
  educationLevels?: ClassifierValueCard[];
  isReaderCardFormLoading: boolean = true;
  topicToDelete?: string;
  topicToChangeStatus?: string;
  isOpenConfirmDelete: boolean = false;
  isOpenConfirmChangeStatus = false;
  isDeleteUpdateLoading: boolean = false;
  isSubmittingFiles: boolean = false;
  canEditTopics = false;
  page = 1;
  pageSize = 10;
  hasFormChanged: boolean = false;
  isOpenConfirmLeave: boolean = false;

  get education(): AbstractControl<any, any> | null {
    return this.form.get('education');
  }
  get degree(): AbstractControl<any, any> | null {
    return this.form.get('degree');
  }
  get isHigherEducation(): boolean {
    return this.educationLevels?.find((level) => level.id === this.education?.value)?.code === EDUCATION_CODE_HIGHER;
  }

  constructor(
    protected override location: Location,
    protected override router: Router,
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    protected authService: AuthorizationService,
    protected override toastService: ToastService,
    protected override translateService: TranslateService
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
  }

  override ngOnInit(): void {
    this.canEditTopics = this.client.authenticationType === 'Local' || isAdminUser();
    this.fileUploadComponentCount = 1;
    this.fillForm();

    this.researchTopics.sort((a: ResearchTopicByClientIdCard, b: ResearchTopicByClientIdCard) =>
      a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1
    );

    this.researchTopics.forEach(() => {
      this.researchTopicsCollapsed.push(true);
    });

    forkJoin([this.getEducationLevels(), this.getScientificDegrees()]).subscribe({
      next: ([edu, deg]) => {
        this.educationLevels = edu?.values ?? [];
        this.scientificDegrees = deg?.values ?? [];
        this.isReaderCardFormLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.toastService.showRequestError();
        this.isReaderCardFormLoading = false;
      }
    });
  }

  override setIsEditMode(isEditMode: boolean, confirmed: boolean = false): void {
    if (!confirmed && !isEditMode) {
      this.isOpenConfirmLeave = true;
    } else {
      super.setIsEditMode(isEditMode);
      if (!isEditMode) {
        this.fillForm();
        this.form.markAsPristine();
        this.hasFormChanged = false;
        this.formChanged.emit(false);
      }
    }
  }

  getEducationLevels() {
    return this.apiService.getClassifierValues(EDUCATION_CLASSIFIER_CODE).pipe(
      map((card) => ({
        ...card,
        values: sortByCodeOrder(card.values, EDUCATION_ORDER)
      }))
    );
  }

  getScientificDegrees() {
    return this.apiService.getClassifierValues(SCIENTIFIC_DEGREE_CLASSIFIER_CODE).pipe(
      map((card) => ({
        ...card,
        values: sortByCodeOrder(card.values, DEGREE_ORDER)
      }))
    );
  }

  override fillForm(): void {
    const educationLevelId = this.readersCard?.educationLevelId || null;
    const scientificDegreeId = this.readersCard?.scientificDegreeId || null;

    this.form = this.formBuilder.group({
      education: [educationLevelId, [Validators.required]],
      degree: [scientificDegreeId]
    });

    this.form.valueChanges.subscribe(() => {
      this.hasFormChanged = this.form.dirty;
      this.formChanged.emit(this.form.dirty);
    });

    this.form.markAsPristine();
    this.hasFormChanged = false;
  }

  override submit(): void {
    this.submitAttempted = true;

    if (this.form.valid || this.customFormValidation()) {
      this.isReaderCardFormLoading = true;

      if (!this.client?.hasReaderCard) {
        const args: CreateReaderCardRequest = {
          educationLevelId: this.form.get('education')?.value,
          scientificDegreeId: this.isHigherEducation ? this.form.get('degree')?.value : null,
          clientId: this.client.id
        };

        this.apiService.createReadersCard(args).subscribe(
          (readersCard: ReaderCardById) => {
            this.readersCard = {
              ...readersCard,
              doctoralCertificateFiles: []
            };
            this.isSubmittingFiles = true;
            this.hasFormChanged = false;
            this.formChanged.emit(false);
          },
          (error) => {
            console.error(error);
            this.toastService.showRequestError();
            this.isReaderCardFormLoading = false;
          }
        );
      } else {
        const args: UpdateReaderCardRequest = {
          id: this.readersCard?.id || '',
          educationLevelId: this.form.get('education')?.value || null,
          scientificDegreeId: this.isHigherEducation ? this.form.get('degree')?.value : null
        };

        this.apiService.updateReadersCard(args).subscribe(
          () => {
            this.isSubmittingFiles = true;
            this.hasFormChanged = false;
            this.formChanged.emit(false);
          },
          (error: any) => {
            console.error(error);
            this.toastService.showRequestError();
            this.isReaderCardFormLoading = false;
          }
        );
      }
    } else {
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'pages.client.readersCard');
    }
  }

  override onSubmitSuccess(): void {
    if (this.client.hasReaderCard) {
      this.toastService.showSuccess('updateReadersCardSuccess', '', {
        shouldTranslate: true
      });
    } else {
      this.toastService.showSuccess('createReadersCardSuccess', '', {
        shouldTranslate: true
      });
    }

    this.isReaderCardFormLoading = false;
    this.isSubmittingFiles = false;
    this.client.hasReaderCard = true;
    this.fileActions = [];
    this.isEditMode = false;
    this.uploadComponentsReceived = 0;
    this.readersCardUpdated.emit();
  }

  deleteTopic(topicId: string | undefined): void {
    if (!this.topicToDelete) {
      this.isOpenConfirmDelete = true;
      this.topicToDelete = topicId;
    } else if (this.topicToDelete) {
      this.isDeleteUpdateLoading = true;
      this.apiService.deleteResearchTopic(this.topicToDelete).subscribe(
        () => {
          this.isDeleteUpdateLoading = false;
          this.researchTopics = this.researchTopics.filter((topic) => topic.id !== topicId);
          this.topicToDelete = undefined;
        },
        (error: any) => {
          console.error(error);
          this.isDeleteUpdateLoading = false;
          this.toastService.showRequestError();
        }
      );
    }
  }

  onModalClose(): void {
    this.isOpenConfirmDelete = false;
    this.isOpenConfirmChangeStatus = false;
    this.topicToDelete = undefined;
    this.topicToChangeStatus = undefined;
    this.isOpenConfirmLeave = false;
  }

  updateTopicStatus(topicId: string | undefined): void {
    const isTopicActive = this.researchTopics.find((topic) => topic.id === topicId)?.isActive;

    if (!this.topicToChangeStatus && isTopicActive) {
      this.isOpenConfirmChangeStatus = true;
      this.topicToChangeStatus = topicId;
    } else if (this.topicToChangeStatus || !isTopicActive) {
      this.isDeleteUpdateLoading = true;
      const topicToUpdate = this.researchTopics.find((topic) => topic.id === topicId);

      if (topicToUpdate) {
        const args: UpdateResearchTopicActivityRequest = {
          researchTopicId: topicToUpdate.id,
          isActive: !topicToUpdate.isActive
        };
        this.apiService.updateResearchTopicActivity(args).subscribe(
          (data: ResearchTopicByIdCard) => {
            this.researchTopics.forEach((topic) => {
              if (topic.id === data.id) {
                topic.isActive = data.isActive;
              }
            });
            this.topicToChangeStatus = undefined;
            this.isDeleteUpdateLoading = false;
            this.refetchTopics.emit();

            if (data.isActive) {
              this.toastService.showSuccess('topicActivatedSuccess', '', {
                shouldTranslate: true
              });
            } else {
              this.toastService.showSuccess('topicDeactivatedSuccess', '', {
                shouldTranslate: true
              });
            }
          },
          (error: any) => {
            console.error(error);
            const errorObj = error?.error?.errors;

            if (errorObj && typeof errorObj === 'object') {
              const firstKey = Object.keys(errorObj)[0];
              const draftIdsStr = errorObj[firstKey]?.[0];

              if (draftIdsStr) {
                const draftIds = draftIdsStr.split(',').map((id: string) => id.trim());
                let timesUsedInUserDrafts = 0;
                let processedCount = 0;
                let lnaToastShown = false;

                const maybeShowLnaToast = () => {
                  if (processedCount === draftIds.length) {
                    if (timesUsedInUserDrafts > 0 && !lnaToastShown) {
                      lnaToastShown = true;
                      const text = this.translateService.instant('components.notification.usedInLnaSubmission', {
                        info: timesUsedInUserDrafts
                      });
                      this.toastService.showDanger(text, '', {
                        shouldTranslate: false
                      });
                    }
                  }
                };

                const handleReadingRoomSubmission = (draftId: string) => {
                  this.apiService.getReadingRoomSubmission(draftId).subscribe({
                    next: (data: BackofficeReadingRoomSubmissionCard) => {
                      processedCount++;

                      if (!data?.readingRoom?.name) {
                        timesUsedInUserDrafts++;
                      } else {
                        const textParams = `${data.readingRoom.name}: ${formatDate(data.visitDate)}, ${data.visitTimeTo ? formatTime(data.visitTimeFrom) + ':' + formatTime(data.visitTimeTo) : formatTime(data.visitTimeFrom)}`;
                        const text = this.translateService.instant('components.notification.usedInDraftSubmission', {
                          data: textParams
                        });
                        this.toastService.showDanger(text, '', {
                          shouldTranslate: false
                        });
                      }

                      maybeShowLnaToast();
                    },
                    error: () => {
                      processedCount++;
                      maybeShowLnaToast();
                    }
                  });
                };

                draftIds.forEach((draftId: string) => handleReadingRoomSubmission(draftId));
              } else {
                this.toastService.showRequestError();
              }
            } else {
              this.toastService.showRequestError();
            }

            this.isDeleteUpdateLoading = false;
          }
        );
      }
    }
  }

  customFormValidation(): boolean {
    if (!this.isHigherEducation && this.education?.value) {
      return true;
    }

    return false;
  }

  getTopicToChangeStatusIsActive(): boolean | undefined {
    return this.researchTopics.find((topic) => topic.id === this.topicToChangeStatus)?.isActive;
  }

  onPageSizeChange(event: any): void {
    this.pageSize = event.target.value;
    this.page = 1;
  }

  hasExpiredFiles(researchTopic: ResearchTopicByClientIdCard): boolean {
    const today = new Date();

    const allFiles = [...(researchTopic.topicFiles || []), ...(researchTopic.justificationFiles || [])];

    return allFiles.some((file) => {
      const note = file.fileNote;
      if (note?.termType === 'Fixed' && note.validTill) {
        const validTillDate = new Date(note.validTill);
        return validTillDate < today;
      }
      return false;
    });
  }

  isDoctoralDegreeSelected(): boolean {
    const degree = this.scientificDegrees?.find((degree) => degree.id === this.degree?.value);
    return degree?.code === SCIENTIFIC_DEGREE_CODE_DOCTORAL;
  }

  sortEducationLevels(): void {
    this.educationLevels = this.educationLevels?.reverse();
    if (this.educationLevels && this.educationLevels.length >= 2) {
      const temp = this.educationLevels[0];
      this.educationLevels[0] = this.educationLevels[1];
      this.educationLevels[1] = temp;
    }
  }
}
